import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { fmtNum } from 'TestDataManagement/tdmUtils';
// The status pills are TDM's (.tdm-pill); the graph Test renders in the operator console, which does
// not load the TDM module's stylesheet, so it comes with the component.
import 'TestDataManagement/TestDataManagement.css';

// The isolation report (contract 3.0, 2026-11-03; it replaced the object report): per source, what each
// rule and integrity link reached, what is isolated, and what a provision will need. The platform builds
// it the same way for the graph Test, a subset preview, every run and the console's draft check
// (`counted: false`, plan only); these pieces render it in all of them. Run LISTS carry only its counts
// (`objectSummary`). Inform, never block: nothing here stops a test, a preview, a run or a save.
//
// A report: { slug, dataSourceId, dataSourceName, dataGraphId, dataGraphName, outcome, startTable, startColumn,
// counted, objects: [{ name, role: START | RULE | REFERENCE, save, listed, objectKind, status: OK | FAILED | EMPTY,
// rows, ruleRows, integrityRows, rules: [{ id, valuesSent, rowsMatched, maxRowsPerValue, capped, maxPerParent }],
// integrityAdded: [{ via, edgeId, rows }] }], needed: [{ object, outcome: ROWS_FAIL | FIELD_BLANKED | RUN_USER |
// MATCHED, rows, distinctValues, inProfile, objectKind, references: [{ from, column, required, rows, distinctValues }] }],
// findings: [{ code, severity: ERROR | WARNING | INFO, object, rule, message }], notDownloaded (CONNECTED only),
// warnings, counts: { objects, failed, errors, warnings, infos, needed } }. Counts are null until counted.

/** A preview's reports (an array) or a run's ({ sources: [...] }), as one list. */
export const reportSources = (r) => {
  if (Array.isArray(r)) return r;
  return r && Array.isArray(r.sources) ? r.sources : [];
};

const len = (a) => (Array.isArray(a) ? a.length : 0);
const countOr = (v, fallback) => (v != null && Number.isFinite(Number(v)) ? Number(v) : fallback);
const known = (v) => v != null && v !== '' && Number.isFinite(Number(v));

/** A report's TRUE totals, from its `counts`; a key it does not carry is counted from its lists. */
export const reportCounts = (r) => {
  const c = (r && r.counts) || {};
  const findings = (r && r.findings) || [];
  const bySeverity = (s) => findings.filter((f) => f && f.severity === s).length;
  return {
    objects: countOr(c.objects, len(r && r.objects)),
    failed: countOr(c.failed, ((r && r.objects) || []).filter((o) => o && o.status === 'FAILED').length),
    errors: countOr(c.errors, bySeverity('ERROR')),
    warnings: countOr(c.warnings, bySeverity('WARNING')),
    infos: countOr(c.infos, bySeverity('INFO')),
    needed: countOr(c.needed, len(r && r.needed)),
  };
};

const plural = (n, word) => `${fmtNum(n)} ${word}${Number(n) === 1 ? '' : 's'}`;

/**
 * A CONNECTED report's `notDownloaded` block as one sentence: "29,960 tables not downloaded: sales 18,200 ·
 * hr 4,100 · 812 other schemas 7,660." A lone schema-less bucket (Salesforce objects, schema-less
 * collections) says only the total. null without a block.
 */
export const notDownloadedText = (nd, noun = 'Table') => {
  if (!nd || !(Number(nd.total) > 0)) return null;
  const buckets = Array.isArray(nd.bySchema) ? nd.bySchema : [];
  const other = nd.otherSchemas && Number(nd.otherSchemas.schemas) > 0 ? nd.otherSchemas : null;
  const parts = buckets.map((b) => `${b.schema == null ? 'no schema' : b.schema} ${fmtNum(b.count)}`);
  if (other) parts.push(`${plural(other.schemas, 'other schema')} ${fmtNum(other.count)}`);
  const schemaless = !other && buckets.length > 0 && buckets.every((b) => b.schema == null);
  const breakdown = parts.length === 0 || schemaless ? '' : `: ${parts.join(' · ')}`;
  const listed = nd.listed != null && Number(nd.listed) > 0 && Number(nd.listed) < Number(nd.total)
    ? ` The first ${fmtNum(nd.listed)} are listed.` : '';
  return `${plural(nd.total, String(noun).toLowerCase())} not downloaded${breakdown}.${listed}`;
};

/** The not-downloaded breakdown of a CONNECTED report; nothing when the report has no block. */
export const NotDownloadedNote = ({ report, noun, className = 'dg-muted mb-2', testId }) => {
  const text = notDownloadedText(report && report.notDownloaded, noun);
  return text ? <div className={className} data-testid={testId}>{text}</div> : null;
};

/** What the source calls a table, for column headers. */
export const reportNoun = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Object';
  return e === 'mongodb' ? 'Collection' : 'Table';
};

/** "A, B, C +4 more" */
export const namesPreview = (names, limit = 10) => (names.length > limit
  ? `${names.slice(0, limit).join(', ')} +${fmtNum(names.length - limit)} more`
  : names.join(', '));

const STATUS_LABEL = { FAILED: 'FAILED', EMPTY: 'NO ROWS' };
/** An object's status: FAILED (isolated) as a danger pill, EMPTY neutral, OK nothing. */
export const StatusPill = ({ status }) => (status && status !== 'OK' ? (
  <span className={`tdm-pill${status === 'FAILED' ? ' tdm-pill--danger' : ''}`}>{STATUS_LABEL[status] || status}</span>
) : null);

/** A table's rows past `limit` stay folded behind "Show all" — a CONNECTED plan can reach hundreds. */
export const FoldedRows = ({ items, limit, testId, children: renderRow }) => {
  const [open, setOpen] = useState(false);
  const shown = open || items.length <= limit ? items : items.slice(0, limit);
  return (
    <>
      {shown.map(renderRow)}
      {items.length > limit && (
        <div className='dg-table__row dg-table__row--more' role='row'>
          <button type='button' className='dg-link' onClick={() => setOpen(!open)} data-testid={`${testId}-more`}>
            {open ? 'Show fewer' : `Show all ${fmtNum(items.length)} (${fmtNum(items.length - limit)} more)`}
          </button>
        </div>
      )}
    </>
  );
};

/** One titled list of a report: "Objects · 12", an optional hint, then its table. */
export const ReportSection = ({ title, count, hint, testId, children }) => (
  <div className='dg-section' data-testid={testId}>
    <div className='dg-section__title'>{title}{count != null ? ` · ${fmtNum(count)}` : ''}</div>
    {hint && <div className='dg-muted mb-2'>{hint}</div>}
    {children}
  </div>
);

export const ReportWarnings = ({ warnings, testId }) => ((warnings || []).length === 0 ? null : (
  <ul className='dg-warnings' data-testid={testId}>
    {warnings.map((w, i) => <li key={i}>{w}</li>)}
  </ul>
));

// ---- findings ----

const SEVERITY_RANK = { ERROR: 0, WARNING: 1, INFO: 2 };
const SEVERITY_LABEL = { ERROR: 'Error', WARNING: 'Warning', INFO: 'Info' };

/** Worst first; the server's order within a severity is kept. */
export const sortFindings = (findings) => (findings || [])
  .map((f, i) => ({ f, i }))
  .sort((a, b) => ((SEVERITY_RANK[a.f.severity] ?? 3) - (SEVERITY_RANK[b.f.severity] ?? 3)) || (a.i - b.i))
  .map((x) => x.f);

const FINDING_WORDS = {
  ISOLATED: 'no rule connects it to the start and nothing saved looks it up',
  NOT_IN_PROFILE: 'not in the latest profile',
  EMPTY_RULE: 'a rule was sent values and matched nothing',
  NOT_REACHED: 'a rule’s source has no rows',
  INTEGRITY_ADDED: 'integrity added rows',
  NEEDED: 'looked up but not in the subset',
  FANOUT: 'a rule returns many rows per value',
  CAPPED: '"at most" dropped rows',
};
/** The finding's own sentence (the platform words them), else what its code means. */
export const findingText = (f) => {
  if (!f) return '';
  if (f.message) return f.message;
  const what = FINDING_WORDS[f.code] || f.code;
  const where = [f.object, f.rule].filter(Boolean).join(' · ');
  return where ? `${where}: ${what}` : what;
};

/** Every finding, worst first, coloured by severity, past `limit` folded. */
export const FindingsList = ({ findings, limit = 20, testId }) => {
  const sorted = sortFindings(findings);
  if (sorted.length === 0) return null;
  return (
    <div className='dg-findings' role='list' data-testid={testId}>
      <FoldedRows items={sorted} limit={limit} testId={testId}>
        {(f, i) => (
          <div role='listitem' key={`${f.code}-${f.object || ''}-${f.rule || ''}-${i}`}
            className={`dg-finding dg-finding--${String(f.severity || 'info').toLowerCase()}`}
            data-testid={`${testId}-${f.code}${f.object ? `-${f.object}` : ''}`}>
            <span className='dg-finding__sev'>{SEVERITY_LABEL[f.severity] || f.severity}</span>
            <span className='dg-finding__code dg-mono'>{f.code}</span>
            <span className='dg-finding__text'>{findingText(f)}</span>
          </div>
        )}
      </FoldedRows>
    </div>
  );
};

// ---- needed by this list ----

export const NEEDED_OUTCOME = {
  ROWS_FAIL: { label: 'Rows fail', tone: 'danger', rank: 0, hint: 'a required lookup: rows that point at it are rejected on provision' },
  FIELD_BLANKED: { label: 'Field blanked', tone: 'warning', rank: 1, hint: 'an optional lookup: provisioning sets it empty' },
  RUN_USER: { label: 'Reassigned to the run user', tone: 'info', rank: 2, hint: 'an owner lookup: provisioning gives the rows to the run user' },
  MATCHED: { label: 'Matched in the target', tone: 'success', rank: 3, hint: 'org configuration, matched by its natural key in the target' },
};
export const neededLabel = (outcome) => (NEEDED_OUTCOME[outcome] || {}).label || outcome || '';

/** Worst first (rows fail, field blanked, run user, matched), then most rows, then name. */
export const sortNeeded = (needed) => [...(needed || [])].sort((a, b) => (
  ((NEEDED_OUTCOME[a.outcome] || { rank: 9 }).rank - (NEEDED_OUTCOME[b.outcome] || { rank: 9 }).rank)
  || ((Number(b.rows) || 0) - (Number(a.rows) || 0))
  || String(a.object || '').localeCompare(String(b.object || ''))
));

const refText = (r) => `${r.from}.${r.column}`;

/**
 * One needed object in words. Before counts: "ServiceTerritoryWorkType.WorkTypeId (required)". After:
 * "11 ServiceTerritoryWorkType rows point at 5 WorkType records not in the subset: 11 rows will fail".
 */
export const neededText = (n, counted) => {
  const refs = (n && n.references) || [];
  if (!counted || !known(n.rows)) {
    return refs.map((r) => `${refText(r)}${r.required ? ' (required)' : ''}`).join(', ');
  }
  const from = refs.length === 1 ? `${refs[0].from} ` : '';
  const records = known(n.distinctValues) ? `${fmtNum(n.distinctValues)} ${n.object} record${Number(n.distinctValues) === 1 ? '' : 's'}` : n.object;
  if (n.outcome === 'MATCHED') return `${fmtNum(n.rows)} ${from}row${Number(n.rows) === 1 ? '' : 's'} point at ${records}, matched in the target`;
  const tail = {
    ROWS_FAIL: `: ${plural(n.rows, 'row')} will fail`,
    FIELD_BLANKED: ': the field is left empty',
    RUN_USER: ': reassigned to the run user',
  }[n.outcome] || '';
  return `${fmtNum(n.rows)} ${from}row${Number(n.rows) === 1 ? '' : 's'} point at ${records} not in the subset${tail}`;
};

/**
 * "Needed by this list": every object off the list that a saved object looks up, worst first, with what
 * provisioning will do with those references. `onAdd(name)` (the graph editor, a list graph) shows "Add to
 * list" — it adds the object as reference-only, so integrity brings its rows. A configuration object is
 * already staged (matched, not inserted), and one the profile does not have cannot be listed.
 */
export const NeededPanel = ({ needed, counted, onAdd, canAdd = true, listed, testId = 'dg-needed', noun = 'Object' }) => {
  const rows = sortNeeded(needed);
  const isListed = (name) => Boolean(listed && listed.has(String(name).toLowerCase()));
  return (
    <div className='dg-needed' data-testid={testId}>
      <div className='dg-section__title'>Needed by this list · {fmtNum(rows.length)}</div>
      {rows.length === 0
        ? <div className='dg-muted' data-testid={`${testId}-none`}>Every lookup of a saved {noun.toLowerCase()} lands on a saved {noun.toLowerCase()}.</div>
        : (
          <div className='dg-table' role='table'>
            <FoldedRows items={rows} limit={20} testId={testId}>
              {(n) => {
                const o = NEEDED_OUTCOME[n.outcome] || {};
                const addable = onAdd && n.outcome !== 'MATCHED' && n.inProfile !== false && !isListed(n.object);
                return (
                  <div className='dg-table__row dg-table__row--needed' role='row' key={n.object} data-testid={`${testId}-${n.object}`}>
                    <span className='dg-mono' title={n.object}>{n.object}</span>
                    <span><span className={`tdm-pill${o.tone ? ` tdm-pill--${o.tone}` : ''}`} title={o.hint}>{neededLabel(n.outcome)}</span></span>
                    <span className='dg-muted dg-needed__what' data-testid={`${testId}-${n.object}-text`}>
                      {neededText(n, counted)}
                      {n.inProfile === false ? ' · not in the profile' : ''}
                    </span>
                    <span className='dg-actions'>
                      {addable && (
                        <Button size='sm' variant='outline-primary' disabled={!canAdd} data-testid={`${testId}-add-${n.object}`}
                          title='Add it to the list as a reference-only object: integrity brings its rows'
                          onClick={() => onAdd(n.object)}>Add to list</Button>
                      )}
                    </span>
                  </div>
                );
              }}
            </FoldedRows>
          </div>
        )}
    </div>
  );
};

// ---- objects ----

const ROLE_LABEL = { START: 'Start', RULE: 'Rule', REFERENCE: 'Reference' };
export const roleLabel = (role) => ROLE_LABEL[role] || role || '';

/** "5 values → 4 rows", with "· 5 capped" when "at most" dropped rows; null before counts. */
export const ruleStatText = (r) => {
  if (!r || !known(r.valuesSent)) return null;
  const text = `${plural(r.valuesSent, 'value')} → ${plural(r.rowsMatched || 0, 'row')}`;
  return Number(r.capped) > 0 ? `${text} · ${fmtNum(r.capped)} capped` : text;
};

/** Why a counted object has no rows, in words: its finding's sentence when there is one. null otherwise. */
export const whyZero = (o, report) => {
  if (!o || !(report && report.counted) || !known(o.rows) || Number(o.rows) > 0) return null;
  const mine = sortFindings(((report && report.findings) || [])
    .filter((f) => f && String(f.object || '').toLowerCase() === String(o.name).toLowerCase()
      && ['ISOLATED', 'NOT_REACHED', 'EMPTY_RULE', 'NOT_IN_PROFILE'].includes(f.code)));
  if (mine.length > 0) return findingText(mine[0]);
  const rules = o.rules || [];
  if (o.role === 'REFERENCE' || rules.length === 0) return 'nothing saved looks it up for these records';
  if (rules.every((r) => !(Number(r.valuesSent) > 0))) return 'its rules’ sources have no rows for these records';
  return 'its rules matched nothing';
};

const rowsCell = (o) => {
  if (!known(o.rows)) return '—';
  const integ = Number(o.integrityRows) || 0;
  return integ > 0 ? `${fmtNum(o.ruleRows || 0)} + ${fmtNum(integ)}` : fmtNum(o.rows);
};

/** Every object of the plan: role, rows (rule + integrity), each rule's counts, integrity additions, why 0. */
export const ReportObjectsTable = ({ report, noun = 'Object', limit = 20, testId }) => {
  const objects = (report && report.objects) || [];
  const start = report && report.startTable;
  return (
    <div className='dg-table' role='table' data-testid={testId}>
      <div className='dg-table__head dg-table__head--objects' role='row'>
        <span>{noun}</span><span>Role</span><span className='dg-num' title='Rows the rules reached + rows integrity added'>Rows</span><span>Reached by</span>
      </div>
      <FoldedRows items={objects} limit={limit} testId={testId}>
        {(o) => {
          const why = whyZero(o, report);
          return (
            <div className='dg-table__row dg-table__row--objects' role='row' key={o.name} data-testid={`${testId}-${o.name}`}>
              <span className='dg-report-name'>
                <span className='dg-mono' title={o.name}>{o.name}</span>
                {o.role === 'START' && <span className='dg-muted'> (start)</span>}
                {o.save === false && <span className='dg-badge' title='Its rows feed rules and are never staged'>use, don&apos;t save</span>}
                {o.listed === false && <span className='dg-badge' title='Not on the list: staged so provisioning can match it by natural key'>configuration</span>}
                <StatusPill status={o.status} />
              </span>
              <span className='dg-muted'>{roleLabel(o.role)}</span>
              <span className='dg-num' data-testid={`${testId}-${o.name}-rows`}>{rowsCell(o)}</span>
              <span className='dg-report-via'>
                {o.role === 'START' && <div className='dg-muted'>the selection{start && start !== o.name ? ` on ${start}` : ''}</div>}
                {(o.rules || []).map((r) => (
                  <div key={r.id} className='dg-report-rule' data-testid={`${testId}-${o.name}-rule`}>
                    <span className='dg-mono' title={r.id}>{r.id}</span>
                    {r.maxPerParent != null && <span className='dg-muted'> · at most {fmtNum(r.maxPerParent)} per parent</span>}
                    {ruleStatText(r) && <span className='dg-muted'> · {ruleStatText(r)}</span>}
                  </div>
                ))}
                {(o.integrityAdded || []).filter((a) => Number(a.rows) > 0).map((a) => (
                  <div key={a.edgeId || a.via} className='dg-muted' data-testid={`${testId}-${o.name}-integrity`}>+{fmtNum(a.rows)} via {a.via}</div>
                ))}
                {o.role === 'REFERENCE' && (o.rules || []).length === 0 && !(o.integrityAdded || []).some((a) => Number(a.rows) > 0) && (
                  <div className='dg-muted'>integrity only</div>
                )}
                {why && <div className='dg-why' data-testid={`${testId}-${o.name}-why`}>Why 0: {why}</div>}
              </span>
            </div>
          );
        }}
      </FoldedRows>
    </div>
  );
};

const OUTCOME_LABEL = { CONNECTED: 'Everything connected', LIST: 'A list of objects' };

/**
 * One source's isolation report in full: findings, then every object with its rules' counts, then what
 * the list needs. `onAdd` passes through to the Needed panel; `showObjects: false` keeps it to findings
 * and needed (the subset preview, whose estimates already list the tables).
 */
export const IsolationReport = ({ report, noun = 'Object', onAdd, canAdd, listed, limit = 20, testId, showObjects = true }) => {
  const r = report || {};
  const counts = reportCounts(r);
  return (
    <div className='dg-report' data-testid={testId}>
      <div className='dg-muted dg-note' data-testid={`${testId}-head`}>
        {OUTCOME_LABEL[r.outcome] || 'Everything connected'}
        {r.startTable ? ` · starts at ${r.startTable}${r.startColumn ? `.${r.startColumn}` : ''}` : ''}
        {r.counted ? '' : ' · from the plan, not counted yet'}
      </div>
      <FindingsList findings={r.findings} testId={`${testId}-findings`} />
      <ReportWarnings warnings={r.warnings} testId={`${testId}-warnings`} />
      {showObjects && (
        <ReportSection title={`${noun}s`} count={counts.objects} testId={`${testId}-objects`}>
          <ReportObjectsTable report={r} noun={noun} limit={limit} testId={`${testId}-objects-list`} />
        </ReportSection>
      )}
      <NotDownloadedNote report={r} noun={noun} testId={`${testId}-not-downloaded`} className='dg-muted mt-2' />
      <div className='dg-section'>
        <NeededPanel needed={r.needed} counted={Boolean(r.counted)} onAdd={onAdd} canAdd={canAdd} listed={listed}
          testId={`${testId}-needed`} noun={noun} />
      </div>
    </div>
  );
};

/**
 * Tags for the graph view (2026-11-03): listed objects and the objects the list needs, keyed by lowercased
 * name — `{ text, tone, title }`. From a draft or a counted report.
 */
export const planTags = (report) => {
  const out = {};
  ((report && report.objects) || []).forEach((o) => {
    if (!o || !o.name) return;
    const text = o.role === 'START' ? 'Start' : (o.listed === false ? 'Configuration' : 'In the subset');
    out[String(o.name).toLowerCase()] = { text: o.save === false ? 'Used, not saved' : text, tone: o.status === 'FAILED' ? 'danger' : 'info', title: roleLabel(o.role) };
  });
  ((report && report.needed) || []).forEach((n) => {
    if (!n || !n.object || n.outcome === 'MATCHED') return;
    const o = NEEDED_OUTCOME[n.outcome] || {};
    out[String(n.object).toLowerCase()] = { text: `Needed: ${neededLabel(n.outcome).toLowerCase()}`, tone: o.tone || 'warning', title: o.hint };
  });
  return out;
};

/** A graph-view tag, from planTags. */
export const PlanTag = ({ tag, testId }) => (tag ? (
  <span className={`tdm-pill tdm-pill--${tag.tone || 'info'} dg-plan-tag`} title={tag.title} data-testid={testId}>{tag.text}</span>
) : null);

/**
 * A run summary's `objectSummary` ({ sources: [{ counts, warnings }] }, no lists) as the parts of one line,
 * summed over its sources: "1 failed · 2 errors · 3 warnings · 2 needed". Zero parts are left out, so a run
 * with nothing to say gives [].
 */
export const objectSummaryParts = (summary) => {
  const total = { failed: 0, errors: 0, warnings: 0, needed: 0 };
  reportSources(summary).forEach((s) => {
    const c = reportCounts(s);
    total.failed += c.failed;
    total.errors += Math.max(0, c.errors - c.failed);
    total.warnings += c.warnings + (Array.isArray(s && s.warnings) ? s.warnings.length : 0);
    total.needed += c.needed;
  });
  const parts = [];
  if (total.failed > 0) parts.push({ key: 'failed', text: `${fmtNum(total.failed)} failed`, danger: true });
  if (total.errors > 0) parts.push({ key: 'errors', text: plural(total.errors, 'error'), danger: true });
  if (total.warnings > 0) parts.push({ key: 'warnings', text: plural(total.warnings, 'warning') });
  if (total.needed > 0) parts.push({ key: 'needed', text: `${fmtNum(total.needed)} needed` });
  return parts;
};
